/**
 * 폰트 캐싱 유틸리티
 * - 웹폰트 프리로드 및 캐싱
 * - 폰트 로딩 상태 관리
 * - 성능 최적화
 */

class FontCache {
  constructor() {
    this.loadedFonts = new Set();
    this.fontLoadPromises = new Map();
    this.cacheKey = 'font-cache-status';
  }

  /**
   * 폰트 파일 프리로드
   * @param {string} fontFamily - 폰트 패밀리명
   * @param {string} fontUrl - 폰트 파일 URL
   * @param {Object} options - 폰트 옵션 (weight, style 등)
   */
  async preloadFont(fontFamily, fontUrl, options = {}) {
    const { weight = 'normal', style = 'normal', display = 'swap' } = options;
    const fontKey = `${fontFamily}-${weight}-${style}`;

    // 이미 로드된 폰트인지 확인
    if (this.loadedFonts.has(fontKey)) {
      return true;
    }

    // 이미 로딩 중인 폰트인지 확인
    if (this.fontLoadPromises.has(fontKey)) {
      return this.fontLoadPromises.get(fontKey);
    }

    // 폰트 로딩 Promise 생성
    const loadPromise = this.loadFont(fontFamily, fontUrl, { weight, style, display });
    this.fontLoadPromises.set(fontKey, loadPromise);

    try {
      await loadPromise;
      this.loadedFonts.add(fontKey);
      this.saveCacheStatus();
      console.log(`✅ [FONT CACHE] Font loaded: ${fontFamily} (${weight}, ${style})`);
      return true;
    } catch (error) {
      console.error(`❌ [FONT CACHE] Failed to load font: ${fontFamily}`, error);
      this.fontLoadPromises.delete(fontKey);
      return false;
    }
  }

  /**
   * 실제 폰트 로딩 로직
   * @param {string} fontFamily - 폰트 패밀리명
   * @param {string} fontUrl - 폰트 파일 URL
   * @param {Object} options - 폰트 옵션
   */
  async loadFont(fontFamily, fontUrl, options) {
    const { weight, style, display } = options;

    // CSS Font Loading API 사용
    if ('fonts' in document) {
      try {
        const fontFace = new FontFace(fontFamily, `url(${fontUrl})`, {
          weight,
          style,
          display
        });

        await fontFace.load();
        document.fonts.add(fontFace);
        return;
      } catch (error) {
        console.warn(`Font loading API failed for ${fontFamily}, using fallback`);
      }
    }

    // Fallback: 동적 스타일 추가
    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = fontUrl;
      link.crossOrigin = 'anonymous';

      link.onload = () => {
        setTimeout(resolve, 100); // 폰트 적용 시간 여유
      };

      link.onerror = () => {
        reject(new Error(`Failed to load font: ${fontUrl}`));
      };

      document.head.appendChild(link);
    });
  }

  /**
   * 여러 폰트 동시 로딩
   * @param {Array} fontConfigs - 폰트 설정 배열
   */
  async preloadFonts(fontConfigs) {
    const loadPromises = fontConfigs.map(config => 
      this.preloadFont(config.family, config.url, config.options)
    );

    try {
      const results = await Promise.allSettled(loadPromises);
      const successful = results.filter(r => r.status === 'fulfilled' && r.value).length;
      const failed = results.length - successful;

      console.log(`🎯 [FONT CACHE] Loaded ${successful}/${results.length} fonts`);
      if (failed > 0) {
        console.warn(`⚠️ [FONT CACHE] Failed to load ${failed} fonts`);
      }

      return { successful, failed, total: results.length };
    } catch (error) {
      console.error('❌ [FONT CACHE] Font loading error:', error);
      return { successful: 0, failed: fontConfigs.length, total: fontConfigs.length };
    }
  }

  /**
   * 캐시 상태 저장
   */
  saveCacheStatus() {
    try {
      const status = {
        fonts: Array.from(this.loadedFonts),
        timestamp: Date.now()
      };
      localStorage.setItem(this.cacheKey, JSON.stringify(status));
    } catch (error) {
      console.warn('Failed to save font cache status:', error);
    }
  }

  /**
   * 캐시 상태 로드
   */
  loadCacheStatus() {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      if (cached) {
        const status = JSON.parse(cached);
        // 24시간 이내의 캐시만 유효
        if (Date.now() - status.timestamp < 24 * 60 * 60 * 1000) {
          this.loadedFonts = new Set(status.fonts);
          return true;
        }
      }
    } catch (error) {
      console.warn('Failed to load font cache status:', error);
    }
    return false;
  }

  /**
   * 특정 폰트 로딩 상태 확인
   * @param {string} fontFamily - 폰트 패밀리명
   * @param {string} weight - 폰트 굵기
   * @param {string} style - 폰트 스타일
   */
  isFontLoaded(fontFamily, weight = 'normal', style = 'normal') {
    const fontKey = `${fontFamily}-${weight}-${style}`;
    return this.loadedFonts.has(fontKey);
  }

  /**
   * 캐시 초기화
   */
  clearCache() {
    this.loadedFonts.clear();
    this.fontLoadPromises.clear();
    localStorage.removeItem(this.cacheKey);
    console.log('🗑️ [FONT CACHE] Cache cleared');
  }
}

// 전역 폰트 캐시 인스턴스
export const fontCache = new FontCache();

// 프로젝트에서 사용하는 주요 폰트 설정
export const PROJECT_FONTS = [
  {
    family: 'source-han-serif-korean',
    url: 'https://use.typekit.net/cld7zuo.css',
    options: { weight: '400', style: 'normal' }
  },
  {
    family: 'source-han-serif-korean',
    url: 'https://use.typekit.net/cld7zuo.css',
    options: { weight: '600', style: 'normal' }
  },
  {
    family: 'source-han-serif-korean',
    url: 'https://use.typekit.net/cld7zuo.css',
    options: { weight: '800', style: 'normal' }
  },
  {
    family: 'source-han-serif-korean',
    url: 'https://use.typekit.net/cld7zuo.css',
    options: { weight: '900', style: 'normal' }
  },
  {
    family: 'Noto Serif KR',
    url: 'https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;600;700;800;900&display=swap',
    options: { weight: '400', style: 'normal' }
  },
  {
    family: 'Noto Serif KR',
    url: 'https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;600;700;800;900&display=swap',
    options: { weight: '600', style: 'normal' }
  },
  {
    family: 'Noto Serif KR',
    url: 'https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;600;700;800;900&display=swap',
    options: { weight: '800', style: 'normal' }
  },
  {
    family: 'Noto Serif KR',
    url: 'https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;600;700;800;900&display=swap',
    options: { weight: '900', style: 'normal' }
  },
];

/**
 * 프로젝트 폰트 초기화
 * 앱 시작 시 호출하여 주요 폰트들을 프리로드
 */
export const initializeProjectFonts = async () => {
  console.log('🔄 [FONT CACHE] Initializing project fonts...');
  
  // 캐시 상태 로드
  fontCache.loadCacheStatus();
  
  // Google Fonts만 캐시 (Adobe Fonts는 자체 시스템 사용)
  const googleFonts = PROJECT_FONTS.filter(font => 
    !font.url.includes('typekit.net')
  );
  
  const result = await fontCache.preloadFonts(googleFonts);
  
  if (result.successful > 0) {
    console.log('✅ [FONT CACHE] Project fonts initialized successfully');
  }
  
  return result;
};

// 하위 호환성을 위한 기존 함수들
export const checkFontLoaded = () => {
  return fontCache.isFontLoaded('source-han-serif-korean');
};

export const markFontLoaded = () => {
  fontCache.loadedFonts.add('source-han-serif-korean-normal-normal');
  fontCache.saveCacheStatus();
};

export const preloadFonts = async () => {
  if (checkFontLoaded()) {
    return Promise.resolve();
  }

  try {
    await fontCache.preloadFont('source-han-serif-korean', 'https://use.typekit.net/cld7zuo.css');
    return Promise.resolve();
  } catch (error) {
    return Promise.resolve(); // 폰트 로딩 실패시에도 페이지는 정상 표시
  }
};

export default fontCache;